import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import i18n from 'i18n';
import _ from 'lodash';
import { messageSourceTypes, RecordingMessage } from 'types/rest/assetCatalogTypes';
import { useDispatch } from 'react-redux';
import { Col, Form, InputGroup, Row, Tab, Tabs } from 'react-bootstrap';
import { DropdownButtonItem } from '@blazemeter/blaze-data-ui';
import { messageTypes } from '../../../../types/rest/dslTypes';
import {
  defaultMessageType,
  messageTypeOptions,
} from '../../Transactions/TransactionEditor/ResponseTab/MessagingResponseTab';
import { CallbackFn, useFormInput, ValidatorType } from '../../../../hooks/useFormInput';
import { convertToNumber } from '../../../../utils/commonUtil';
import BzDropdownButton from '../../../../components/buttons/BzDropdownButton';
import BzFormControlWithHook from '../../../../components/forms/BzFormControlWithHook';
import ResponseBody from '../../../../components/transactionEditor/ResponseBody';
import { title } from '../../Transactions/TransactionEditor/RequestTab/RequestTab';
import ResponseKeyValueList from '../../../../components/transactionEditor/ResponseKeyValueList';
import {
  createRecordingMessage,
  updateRecordingMessage,
} from '../../../../store/recordings/recordingMessages/recordingMessagesActions';
import { showToastNotification } from '../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../store/ui/toasts/toastsTypes';

interface RecordingMessageModalProps {
  recordingMessage: RecordingMessage;
  onClose: () => void;
}

export const destinationOptions: DropdownButtonItem[] = [];
Object.keys(messageSourceTypes).forEach(function(key) {
  destinationOptions.push({
    key: key,
    title: messageSourceTypes[key],
  });
});

export const defaultDestination: DropdownButtonItem = destinationOptions.find(
  dOp => 'QUEUE' === dOp.key,
) as DropdownButtonItem;

export const getSelectedType = (message: RecordingMessage) => {
  const selectedMessageType: DropdownButtonItem =
    message.messageType !== undefined
      ? {
          key: message.messageType,
          title: messageTypes[message.messageType],
        }
      : defaultMessageType;
  return selectedMessageType;
};

export const getSelectedDestination = (message: RecordingMessage) => {
  const selectedDestination: DropdownButtonItem =
    message.destinationType !== undefined
      ? {
          key: message.destinationType,
          title: messageSourceTypes[message.destinationType],
        }
      : defaultDestination;
  return selectedDestination;
};

const RecordingMessageModal = ({ recordingMessage, onClose }: RecordingMessageModalProps) => {
  const [message, setMessage] = useState(recordingMessage);
  const [checkInvalidContent, setCheckInvalidContent] = useState(false);
  const selectedMessageType = getSelectedType(message);
  const selectedDestination = getSelectedDestination(message);

  const updateState = (field: string, value: any) => {
    const updatedState = _.cloneDeep(message);
    _.set(updatedState, field, value);
    setMessage(updatedState);
  };

  const delayInput = useFormInput(
    message.delay ? message.delay : 0,
    fn => {
      if (fn.isValid) {
        updateState('delay', convertToNumber(fn.value));
      }
    },

    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('recordings:editor.validation.required'),
    },
    {
      type: ValidatorType.INTEGER,
      invalidMessage: i18n.t('recordings:editor.validation.numberValidation'),
    },
    {
      type: ValidatorType.MAX,
      value: 999999999,
      invalidMessage: i18n.t('recordings:editor.validation.numberValidationRange'),
    },
  );

  const destinationInput = useFormInput(
    message.destination ? message.destination : '',
    fn => {
      if (fn.isValid) {
        updateState('destination', fn.value);
      }
    },
    { type: ValidatorType.REQUIRED, invalidMessage: i18n.t('recordings:editor.messages.destinationNameRequired') },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 48,
      invalidMessage: i18n.t('recordings:editor.messages.destinationNameMaxLength'),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('recordings:editor.messages.destinationNameIsInvalid'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS_SHORT,
      invalidMessage: i18n.t('recordings:editor.messages.destinationNameIsInvalid'),
    },
  );

  const correlationInput = useFormInput(
    message.correlationId ? message.correlationId : '',
    fn => {
      if (fn.isValid) {
        updateState('correlationId', fn.value);
      }
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('recordings:editor.messages.correlationIdIsInvalid'),
    },
  );

  const nameInput = useFormInput(
    recordingMessage.name,
    (fn: CallbackFn) => updateState('name', fn.value),
    { type: ValidatorType.REQUIRED, invalidMessage: i18n.t('recordings:editor.messages.messageNameRequired') },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('recordings:editor.messages.messageNameMaxLength'),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('recordings:editor.messages.messageNameIsInvalid'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      invalidMessage: i18n.t('recordings:editor.messages.messageNameIsInvalid'),
    },
  );

  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState('body-tab');

  const onSaveHandler = async () => {
    if (message.content === '') {
      setCheckInvalidContent(true);
      setSelectedTab('body-tab');
      return;
    }
    const errorMessage =
      message.id === -1 ? `Could not create Message ${message.name}` : `Could not update Message ${message.name}`;
    try {
      if (message.id === -1) {
        await dispatch(createRecordingMessage(message));
      } else {
        await dispatch(updateRecordingMessage(message));
      }
      onClose();
    } catch (error) {
      const errorDescription = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, errorMessage, errorDescription));
    }
  };

  return (
    <Modal enforceFocus={false} size="lg" show={true} centered={true} backdrop={true} onHide={() => onClose()}>
      <Modal.Header closeButton id="message-modal-id">
        <h5 className="modal-title">{recordingMessage.id === -1 ? 'Add Message' : 'Edit Message'}</h5>
      </Modal.Header>
      <Modal.Body className={'position-relative'}>
        <Row>
          <Col md={4}>
            <Form.Group controlId="qa-input-recording-name-te">
              <div className="form-group-heading">
                <Form.Label>{i18n.t('recordings:editor.messages.name')}</Form.Label>
              </div>
              <div className="position-relative">
                <BzFormControlWithHook
                  autoComplete="off"
                  disabled={false}
                  useFormInputHook={nameInput}
                  placeholder={i18n.t('recordings:editor.messages.enterName')}
                  required
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="input-correlation-id">
              <div className="form-group-heading">
                <Form.Label>{i18n.t('recordings:editor.messages.correlationId')}</Form.Label>
              </div>
              <div className="position-relative">
                <BzFormControlWithHook
                  id={'input-correlation-id'}
                  autoComplete="off"
                  disabled={false}
                  useFormInputHook={correlationInput}
                  placeholder={i18n.t('recordings:editor.messages.enterCorrelationId')}
                  required
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}></Col>
        </Row>
        <Row>
          <Col md={4}>
            <div id={'messageTypeId'} className="mb-4 mt-1">
              <label className={'font-size-lg font-weight-semi-bold'} htmlFor={`input-messageType-id`}>
                {i18n.t('transactions:editor.messageType')}
              </label>
              <BzDropdownButton
                disabled={false}
                id={'input-messageType-id'}
                dropdownItems={messageTypeOptions}
                selectedItem={selectedMessageType}
                onSelectedItem={(item: DropdownButtonItem) => updateState('messageType', item.key)}
                variant="secondary"
                maxHeight={'300px'}
              />
            </div>
          </Col>
          <Col md={4}>
            <div className="mb-4 mt-1">
              <label className={'font-size-lg font-weight-semi-bold'}>Delay</label>
              <InputGroup>
                <BzFormControlWithHook
                  useFormInputHook={delayInput}
                  placeholder={i18n.t('common:thinkTime.placeholders.delay')}
                  required={false}
                  hideErrors={true}
                  autoComplete={'off'}
                />
                <InputGroup.Append>
                  <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                </InputGroup.Append>
                <div className="invalid-feedback">{delayInput.invalidMessage}</div>
              </InputGroup>
            </div>
          </Col>
          <Col md={4} className="text-md-right"></Col>
        </Row>
        <Row>
          <Col md={4}>
            <Form.Group controlId="input-destination-name-id">
              <div className="form-group-heading">
                <Form.Label>{i18n.t('recordings:editor.messages.destinationName')}</Form.Label>
              </div>
              <div className="position-relative">
                <BzFormControlWithHook
                  id={'input-destination-name-id'}
                  autoComplete="off"
                  disabled={false}
                  useFormInputHook={destinationInput}
                  placeholder={i18n.t('recordings:editor.messages.enterDestinationName')}
                  required
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={4}>
            <div id={'messageTypeId'} className="mb-4 mt-1">
              <label className={'font-size-lg font-weight-semi-bold'} htmlFor={'input-destinationType-id'}>
                Destination Type
              </label>
              <BzDropdownButton
                disabled={false}
                id={'input-destinationType-id'}
                dropdownItems={destinationOptions}
                selectedItem={selectedDestination}
                onSelectedItem={(item: DropdownButtonItem) => updateState('destinationType', item.key)}
                variant="secondary"
                maxHeight={'300px'}
              />
            </div>
          </Col>
          <Col md={4}></Col>
        </Row>
        <div className="p-4">
          <Tabs id="qa-tabs-definitions" activeKey={selectedTab} onSelect={(key: string) => setSelectedTab(key)}>
            <Tab title={i18n.t('recordings:editor.bodyTitle')} eventKey="body-tab" style={{ marginTop: '-10px' }}>
              <ResponseBody
                isReadOnly={false}
                onChange={updateState}
                binary={false}
                content={message.content}
                isResponseBodyMagicStringInvalid={false}
                resBodyInvalidMagicStrings={''}
                requestTabState={null}
                hideContentType={true}
                checkInvalidContent={checkInvalidContent}
                setCheckInvalidContent={setCheckInvalidContent}
              />
            </Tab>
            <Tab title={title('Headers', message.headers ? message.headers.length : 0)} eventKey="headers-tab">
              <ResponseKeyValueList
                isReadOnly={false}
                id="header-list-id"
                responseItemList={message.headers}
                emptyListMessage={i18n.t('recordings:editor.noHeadersMatchers')}
                onListChange={list => updateState('headers', list)}
                validateName={true}
              />
            </Tab>
            <Tab
              title={title('Properties', message.properties ? message.properties.length : 0)}
              eventKey="properties-tab"
            >
              <ResponseKeyValueList
                isReadOnly={false}
                id="properties-list-id"
                responseItemList={message.properties}
                emptyListMessage={i18n.t('recordings:editor.noResponseProperties')}
                onListChange={list => updateState('properties', list)}
                validateName={true}
                isMessageProperty={true}
              />
            </Tab>
          </Tabs>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" onClick={onClose}>
          {i18n.t('common:actions.cancel')}
        </Button>

        <Button
          disabled={
            nameInput.valid === false ||
            delayInput.valid === false ||
            destinationInput.valid === false /*||
            (validateBody && message.content === '')*/
          }
          variant="primary"
          onClick={onSaveHandler}
        >
          {i18n.t('common:actions.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RecordingMessageModal;
